import { useEffect, useState } from 'react'

import Uppy from '@uppy/core'
import XHRUpload from '@uppy/xhr-upload'

import { useThunkDispatch } from '@domain-group/react-hooks'

import { baseURL } from '@functions/api'
import { getAccessToken } from '@functions/accessToken'

import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/module'

import { addImage } from '@redux/modules/image'

type UseUppyImageUploadOptions = {
  allowedFileTypes?: string,
  callbacks?: {
    afterAction?: () => void,
    beforeAction?: () => void,
  },
  fieldName?: string,
  image_type?: string,
  imageable_id: number,
  imageable_type: string,
  provider_id?: number,
  requestOptions: EntityOptions,
}

const initializeUppy = (options: UseUppyImageUploadOptions, dispatch: AppDispatch) => {
  const {
    allowedFileTypes,
    callbacks,
    fieldName,
    image_type,
    imageable_id,
    imageable_type,
    provider_id,
    requestOptions = {},
  } = options

  const { afterAction, beforeAction } = callbacks || {}

  const accessToken = getAccessToken()

  const uppy = new Uppy({
    debug: false,
    autoProceed: true,
    onBeforeUpload: () => {
      if (beforeAction) beforeAction()
      return true
    },
    restrictions: {
      allowedFileTypes: allowedFileTypes ? allowedFileTypes.split(',') : ['image/*'],
    },
  })
    .use(XHRUpload, {
      endpoint: `${baseURL}/images`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      formData: true,
      fieldName: fieldName || 'image[file]',
    })
    .on('file-added', (file) => {
      uppy.setFileMeta(file.id, {
        'image[imageable_type]': imageable_type,
        'image[imageable_id]': imageable_id,
        'image[image_type]': image_type || 'photo',
        'image[provider_id]': provider_id,
        ...requestOptions,
      })
    })
    .on('upload-success', (_, response) => {
      const { body: image } = response

      dispatch(addImage(image))

      if (afterAction) afterAction()
    })

  return uppy
}

const checkRequiredOptions = (options = {}) => {
  const requiredOptions = ['imageable_type', 'imageable_id']
  const optionKeys = Object.keys(options)

  return requiredOptions.every(key => optionKeys.includes(key))
}

// @ts-expect-error TS2739
function useUppyImageUpload(options: UseUppyImageUploadOptions = {}, deps: string[] = []) {
  const [uppy, setUppy] = useState<Uppy | undefined>(undefined)

  const dispatch = useThunkDispatch()

  useEffect(() => {
    if (checkRequiredOptions(options)){
      return setUppy(initializeUppy(options, dispatch))
    }

    return () => {
      if (uppy){
        uppy.close()
        setUppy(undefined)
      }
    }
  }, [...deps])

  return uppy
}

export default useUppyImageUpload
